import React, { useEffect, useState } from 'react'
import './AppraisalsAdminDetails.scss'
import { BreadCrumb, ModalWrapper } from 'Components'
import { ApStepOne, ApStepTwo, ApStepThree } from 'Pages/Frontend/Components'
import { appraisalService } from 'Services/appraisalService'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useSearchParams, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { AppraisalsType } from 'Models/appraisalsType'

export type AppraisalFormLMProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment?: string
    manager_score?: number
    manager_comment?: string
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  lineManagerComment?: string
  isDraft: boolean
}

export type AppraisalFormProps = {
  kpi: {
    metric: string
    target: number
    employee_score: number
    employee_comment?: string
    manager_score?: number
    manager_comment?: string
  }[]
  coreValues: {
    question: string
    category: string
    employee_score: number
    manager_score?: number
  }[]
  personalAssessment: {
    category: string
    question: string
    answer: string
    manager_score?: number
  }[]
  lineManagerComment?: string
  isDraft?: boolean
}

export type ApproveAppraisalFormType = {
  managementDecision: string
  managementComment: string
  id?: string | undefined
}
const AppraisalsAdminDetails = () => {
  const { apId } = useParams<{ apId: string }>()
  const [searchParams] = useSearchParams()
  const [request, setRequest] = useState(false)

  // Get a specific query parameter
  const step = searchParams.get('step')

  const [appraisal, setAppraisals] = useState<AppraisalsType>()
  // const currentStep = step ? parseInt(step) : 1; // Convert step to a number and default to 1 if invalid.

  const [currentStep, setCurrentStep] = useState(1)
  const navigate = useNavigate()

  const [showApprovalModal, setShowApprovalModal] = useState(false)

  const toggleApprovalModal = () => setShowApprovalModal(!showApprovalModal)

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<AppraisalFormProps>()

  const {
    register: register2,
    handleSubmit: handleSubmit2,
    formState: { errors: errors2 },
  } = useForm<ApproveAppraisalFormType>()

  const approvalSubmit = async (data: ApproveAppraisalFormType) => {
    // console.log('Form Data:', data)
    setRequest(true)

    await appraisalService.updateAdmin({ id: apId, ...data }).then(
      () => {
        setRequest(false)
        toggleApprovalModal()
        Swal.fire({
          icon: 'success',
          title: 'Completed',
          text: `Appraisal ${data.managementDecision === 'REJECT' ? 'rejected' : 'approved'}`,
        })

        setTimeout(() => {
          navigate('/admin/appraisals-admin')
        }, 2000)
      },
      (error) => {
        setRequest(false)
        Swal.close()
        // console.log('productService error', error.response)
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            cancelButton: 'btn btn-danger',
          },
          buttonsStyling: false,
        })

        swalWithBootstrapButtons.fire({
          title: 'Oops!',
          text: error.response.data.error,
          icon: 'warning',
          showCancelButton: true,
          showConfirmButton: false,
          cancelButtonText: 'close',
          reverseButtons: true,
        })
      },
    )
  }

  const [activeAccordion, setActiveAccordion] = useState(1)
  const [inputError, setInputError] = useState(false)

  const handleNext = async () => {
    const isValid = await trigger() // Validate the current form fields
    if (isValid) {
      setCurrentStep((prev) => prev + 1)
      navigate(`/admin/appraisals-admin/${apId}?step=${currentStep + 1}`)
    } else {
      console.error('Form contains errors, cannot proceed to the next step.')
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
      navigate(`/admin/appraisals-admin/${apId}?step=${currentStep - 1}`)
    }
  }

  const getAppraisals = async (apId: string) => {
    await appraisalService.getSingleAdmin(apId).then(
      (response) => {
        // console.log('response 45', response.data.data.dataset[0])
        setAppraisals(response.data.data.dataset[0])
      },
      (error: { response: { data: { error: string } } }) => {
        console.log('appraisalService error', error.response)
      },
    )
  }

  useEffect(() => {
    if (apId) getAppraisals(apId)
  }, [apId])

  useEffect(() => {
    if (appraisal) {
      // Use the proper template literal type for the field path
      const pLength = appraisal.personalAssessment.length
      for (let x = 0; x < pLength; x++) {
        setValue(`personalAssessment.${x}.answer`, appraisal.personalAssessment[x].answer)
      }

      const kpLength = appraisal.kpi.length
      for (let x = 0; x < kpLength; x++) {
        setValue(`kpi.${x}.employee_score`, appraisal.kpi[x].employee_score)
        setValue(`kpi.${x}.employee_comment`, appraisal.kpi[x].employee_comment)
        setValue(`kpi.${x}.manager_score`, appraisal.kpi[x].manager_score)
        setValue(`kpi.${x}.manager_comment`, appraisal.kpi[x].manager_comment)
      }

      const cvLength = appraisal.coreValues.length
      for (let x = 0; x < cvLength; x++) {
        setValue(`coreValues.${x}.employee_score`, appraisal.coreValues[x].employee_score)

        setValue(`coreValues.${x}.manager_score`, appraisal.coreValues[x].manager_score)

        setValue('lineManagerComment', appraisal.lineManagerComment ?? '')
      }
    }
  }, [appraisal])

  const [settings, setSettings] = useState<{
    id: number
    appraisalYear: number
    appraisalQuarter: number
    appraisalOpen: boolean
    payslipMonth: string
    payslipYear: any
    createdAt: string
  }>()

  const getAppraisalSettings = async () => {
    await appraisalService.settings().then(
      (res) => {
        // console.log('settings', res.data.data)
        setSettings(res.data.data)
      },
      (error) => {
        console.log(error)
      },
    )
  }

  useEffect(() => {
    getAppraisalSettings()
  }, [])

  const isManagerDraft: boolean = appraisal?.status === 'AWAITING_LINE_MANAGER_REVIEW'
  return (
    <div className='AppraisalsAdminDetails mb-5'>
      <div className='container'>
        <BreadCrumb
          title={`Q${settings?.appraisalQuarter} ${settings?.appraisalYear} Appraisal`}
          parent={'Appraisal'}
          parentUrl={'/admin/appraisals'}
          showTitle={false}
        />
      </div>
      <form>
        <div className='container mt-5'>
          <div className='section-top mb-4'>
            <div className='section-title'>
              Q{settings?.appraisalQuarter} {settings?.appraisalYear} Appraisal Form
            </div>
            <div className='section-description'>SECTION A (1 of 3)</div>
          </div>
          {currentStep === 1 && (
            <ApStepOne
              register={register}
              errors={errors}
              isManager={true}
              activeAccordion={activeAccordion}
              setActiveAccordion={setActiveAccordion}
              inputError={inputError}
              setInputError={setInputError}
            />
          )}
          {currentStep === 2 && (
            <ApStepTwo
              kpLength={appraisal?.kpi.length ?? 0}
              register={register}
              errors={errors}
              isManager={true}
              isDisabled={true}
              isAdmin={true}
              watch={watch}
              employeeId={appraisal?.employee.id}
              isManagerDraft={isManagerDraft}
              control={control}
            />
          )}

          {currentStep === 3 && (
            <ApStepThree
              register={register}
              errors={errors}
              isManager={false}
              isDisabled={true}
              isAdmin={true}
              watch={watch}
              isManagerDraft={isManagerDraft}
            />
          )}

          {currentStep === 3 && (
            <div className='card border-0'>
              <div className='card-body'>
                <div className='approvalmodal'>
                  <div className='mb-3'>Line Manager&rsquo;s Comment</div>
                  <div className='form-group mb-3 mt-4'>
                    <label htmlFor='comment'>Comment</label>

                    <textarea
                      disabled={true}
                      {...register('lineManagerComment', {
                        required: 'Field is required',
                      })}
                      id='appraisalQuarter'
                      className='form-control'
                      placeholder='Comment goes here'
                    >
                      {' '}
                    </textarea>
                    {errors.lineManagerComment && (
                      <small className='text-danger'>{errors.lineManagerComment.message}</small>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className='row justify-content-end mt-3'>
            <div className='col-md-3 text-end'>
              {currentStep > 1 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handlePrev}
                >
                  Prev
                </button>
              )}

              {currentStep < 3 && (
                <button
                  type='button'
                  className='btn btn-default m-1 btn-navigate'
                  onClick={handleNext}
                >
                  Next
                </button>
              )}

              {currentStep === 3 && (
                <button
                  className='btn btn-default m-1 btn-navigate'
                  type='button'
                  disabled={request}
                  onClick={toggleApprovalModal}
                >
                  Submit {request && <span className='spinner-border spinner-border-sm'></span>}
                </button>
              )}
            </div>
          </div>
        </div>
      </form>

      <ModalWrapper
        showModal={showApprovalModal}
        closeModal={toggleApprovalModal}
        title='Management Rating'
      >
        <div className='approvalmodal'>
          <div className='mb-3'>
            After discussing the individual&rsquo;s fitness for the role and the proposed
            development interventions/plan, kindly provide management&rsquo;s rating on the approval
            of the outcome, including justification in case of disapproval.
          </div>
          <form onSubmit={handleSubmit2(approvalSubmit)}>
            <label htmlFor=''> Management&rsquo;s Decision</label>

            <div className='form-group mb-3 form-g-1'>
              <input
                type='radio'
                id='year-select'
                {...register2('managementDecision', {
                  required: 'Year is required',
                })}
                value={'APPROVE'}
              />
              <label htmlFor='year-select'> Approval</label>
            </div>
            <div className='form-group mb-3 form-g-1'>
              <input
                type='radio'
                id='year-select'
                {...register2('managementDecision', {
                  required: 'Year is required',
                })}
                value={'REJECT'}
              />

              <label htmlFor='year-select'> Dissaproval</label>
              {errors2.managementDecision && (
                <small className='text-danger'>{errors2.managementDecision.message}</small>
              )}
            </div>

            <div className='form-group mb-3 mt-4'>
              <label htmlFor='comment'>Comment</label>

              <textarea
                {...register2('managementComment', {
                  required: 'Field is required',
                })}
                id='appraisalQuarter'
                className='form-control'
                placeholder='Comment goes here'
              >
                {' '}
              </textarea>
              {errors2.managementComment && (
                <small className='text-danger'>{errors2.managementComment.message}</small>
              )}
            </div>

            <div className='form-group mb-3'>
              <button type='submit' className='btn btn-default btn-0'>
                Complete Assessment
                {request && <span className='spinner-border spinner-border-sm'></span>}
              </button>
            </div>
          </form>
        </div>
      </ModalWrapper>
    </div>
  )
}

export default AppraisalsAdminDetails
